/**
 * 배달 상태 배지 — 보낸 메일이 실제로 상대에게 닿았는지 한 눈에.
 *
 * <h3>왜 status 와 별개인가</h3>
 * <p>{@code status=SENT} 는 "SES 가 접수했다" 까지만 뜻한다. 그 뒤 실제 배달·반송·스팸신고는
 * 몇 초~몇 분 뒤 비동기 이벤트로 도착해 {@code deliveryStatus} 에 기록된다
 * (백엔드 MailSendEventService). 두 값은 다른 축이라 배지도 두 축을 합쳐 하나로 보여준다.
 *
 * <h3>왜 눈에 띄어야 하나</h3>
 * <p>반송률 5% · 스팸신고율 0.1% 를 넘으면 SES 계정 자체가 발송 정지된다. 발신자 본인이
 * 보낸메일함에서 "아, 이 주소 튕겼네" 를 바로 알아채는 것이 가장 빠르고 싼 방어선이다.
 * 그래서 반송·신고는 회색이 아니라 확실히 붉은 계열로 둔다.
 *
 * <h3>표시 규칙(우선순위)</h3>
 * <ol>
 *   <li>{@code status=FAILED} → 발송실패. 애초에 SES 로 나가지도 못한 경우라 배달 결과보다 앞선다.</li>
 *   <li>{@code deliveryStatus} 가 있으면 그 값</li>
 *   <li>둘 다 아니고 {@code status=SENT} → "발송됨"(회색). <b>통지 대기 중</b>이라는 뜻이지
 *       "배달 완료" 가 아니다 — 이 둘을 같은 색으로 칠하면 반송을 놓친다.</li>
 *   <li>그 외(임시저장·예약 등) → 아무것도 그리지 않는다</li>
 * </ol>
 */
import React from "react";
import { Box, Tooltip } from "@mui/material";
import dayjs from "dayjs";

/** SES 배달 이벤트 → 배지 표기. 백엔드 MailSendEventService.상태매핑 과 1:1 대응. */
export const DELIVERY_BADGES = {
    DELIVERED:  { label: "배달완료", color: "#166534", bg: "#dcfce7", border: "#bbf7d0",
                  hint: "상대 메일 서버가 수신을 확인했습니다" },
    BOUNCED:    { label: "반송",     color: "#b91c1c", bg: "#fee2e2", border: "#fecaca",
                  hint: "상대에게 닿지 못하고 튕겼습니다. 주소를 확인하세요" },
    COMPLAINED: { label: "스팸신고", color: "#6b21a8", bg: "#f3e8ff", border: "#e9d5ff",
                  hint: "수신자가 스팸으로 신고했습니다. 반복되면 발송 계정이 정지됩니다" },
    REJECTED:   { label: "거부",     color: "#b91c1c", bg: "#fee2e2", border: "#fecaca",
                  hint: "SES 가 발송을 거부했습니다(바이러스 등)" },
    DELAYED:    { label: "지연",     color: "#b45309", bg: "#fef3c7", border: "#fde68a",
                  hint: "상대 서버가 일시적으로 받지 못해 재시도 중입니다" },
};

const SENT_PENDING = {
    label: "발송됨", color: "#475569", bg: "#f1f5f9", border: "#e2e8f0",
    hint: "발송 서버가 접수했습니다. 배달 결과 통지를 기다리는 중입니다",
};
const SEND_FAILED = {
    label: "발송실패", color: "#b91c1c", bg: "#fee2e2", border: "#fecaca",
    hint: "발송 자체가 실패했습니다. 메일은 나가지 않았습니다",
};

/**
 * 메일 1건의 표시 배지를 고른다. 그릴 게 없으면 null.
 * @param mail MailListItemDto | MailDetailDto 모양 ({status, deliveryStatus, ...})
 */
export const resolveDeliveryBadge = (mail) => {
    if (!mail) return null;
    if (mail.status === "FAILED") return SEND_FAILED;
    const known = DELIVERY_BADGES[mail.deliveryStatus];
    if (known) return known;
    // 알 수 없는 값이 와도 배지를 지우지 않는다 — 값 자체를 그대로 보여줘야 신규 이벤트 타입을 발견할 수 있다.
    if (mail.deliveryStatus) {
        return { label: mail.deliveryStatus, color: "#475569", bg: "#f1f5f9", border: "#e2e8f0", hint: "" };
    }
    if (mail.status === "SENT") return SENT_PENDING;
    return null;
};

/**
 * @param mail     메일 DTO
 * @param showHint true 면 tooltip 없이 사유를 배지 아래 줄로 같이 노출(상세 화면용)
 */
export default function DeliveryStatusBadge({ mail, showHint = false }) {
    const badge = resolveDeliveryBadge(mail);
    if (!badge) return null;

    // 사유 원문 — 반송이면 SES 진단 코드, 발송실패면 우리 서버 예외 메시지.
    const reason = mail.status === "FAILED" ? mail.failureReason : mail.deliveryDetail;
    const at     = mail.deliveryUpdatedAt ? dayjs(mail.deliveryUpdatedAt).format("YYYY-MM-DD HH:mm") : null;

    const tip = [badge.hint, reason, at && `갱신 ${at}`].filter(Boolean).join("\n");

    const chip = (
        <Box component="span" sx={{
            display: "inline-block", px: 0.8, py: 0.15,
            fontSize: 11, fontWeight: 700, lineHeight: 1.6, whiteSpace: "nowrap",
            borderRadius: 1, color: badge.color, bgcolor: badge.bg,
            border: `1px solid ${badge.border}`,
        }}>
            {badge.label}
        </Box>
    );

    if (showHint) {
        return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
                {chip}
                {(badge.hint || reason) && (
                    <Box component="span" sx={{ fontSize: 11.5, color: "#64748b" }}>
                        {reason || badge.hint}
                    </Box>
                )}
            </Box>
        );
    }

    return (
        // whiteSpace: pre-line — 사유·갱신시각을 줄바꿈해 읽히게 한다(한 줄이면 진단 코드가 화면을 가로지른다).
        <Tooltip title={<Box sx={{ whiteSpace: "pre-line", fontSize: 11.5 }}>{tip}</Box>} arrow>
            {chip}
        </Tooltip>
    );
}
